import { Link, useLoaderData } from 'react-router'
import style from './style.css?url'

import { fragmentLinks } from '~/fragments'

import { Accordion, H3, H5, P, Stack } from '~/components-v2'
import { compareOverview } from './content/overview'
import { TbCircleCheck } from 'react-icons/tb'
import { QuoteSectionv2 } from '~/components/quote-section'
import { CallToAction } from '~/utils/a-b'

import compareToolsStyles from '~/styles/pages/compare-tools.css?url'
import quoteStyles from '~/components/quote-section/quote-section.css?url'
import blobStyles from '~/components-v2/blobby/blobby.css?url'
import compareCardStyles from '~/components-v2/compete-comparison-card/comparison-card.css?url'

import { Blobby, BlobbyAww } from '~/components-v2/blobby/blobby'
import { G2ReviewCard } from '../_content.customer-support/g2-review-card'
import { g2Reviews } from '../_content.customer-support/g2-reviews'
import ThunderSalmonHeart from '../_content.customer-support/thunder-salmon-heart.svg?react'
import { CompareCard1 } from './content/compare-cards/card1'
import { CompareCard2 } from './content/compare-cards/card2'
import { CompareCard3 } from './content/compare-cards/card3'
import { CompareCard4 } from './content/compare-cards/card4'
import { CompareCard5 } from '../_content._product.omni-vs-strategy/content/compare-cards/card5'
import { customerLogos } from '~/config/customers'

const title = 'Omni vs Strategy (Formerly MicroStrategy)'
const subTitle =
  "Strategy, formerly MicroStrategy, is ending mainstream support for its legacy platform at the end of 2026, and the company's focus has moved away from enterprise business intelligence. Omni is built for flexibility without giving up governance or waiting on specialists."
const keywords =
  'Why Omni over Strategy, Omni vs Strategy, Omni vs MicroStrategy'
const description =
  'Compare Omni and Strategy (formerly MicroStrategy) across governance, migration, AI, self-service analytics, performance, and data freshness.'

export { meta } from '~/utils'
export const loader = async () => {
  return { keywords, title, subTitle, description }
}

export const links = () => [
  { rel: 'stylesheet', href: compareToolsStyles },
  { rel: 'stylesheet', href: quoteStyles },
  { rel: 'stylesheet', href: blobStyles },
  { rel: 'stylesheet', href: compareCardStyles },
  { rel: 'stylesheet', href: style },
  ...fragmentLinks.securityBadge(),
]

export default function () {
  const { subTitle } = useLoaderData<typeof loader>()

  return (
    <>
      <section className="compare-hero">
        <Stack as="hgroup" className="centered-section-title" gap={4}>
          <h1>
            Why <mark>Omni</mark> over Strategy?
          </h1>
          <h2 className="hero-subhead">{subTitle}</h2>
        </Stack>

        <Stack className="intro-overview" gap={6}>
          {compareOverview.map((item, index) => (
            <Stack
              key={`intro-overview-stack-${index}`}
              row
              gap={2}
              className="intro-overview-item"
            >
              <div className="intro-overview-item-icon">
                <TbCircleCheck />
              </div>
              <P>
                <strong>{item.title}</strong>: {item.summary}
              </P>
            </Stack>
          ))}
        </Stack>

        <Link className="call-to-action primary" to="/demo">
          See Omni
        </Link>
      </section>

      <QuoteSectionv2
        quote={{
          cite: 'Mike Doll, VP of Data at Guitar Center',
          logo: customerLogos.guitarCenter,
          quote: (
            <>
              Choosing Omni is solving for more than just BI. We’ve also primed
              ourselves to leap forward into AI because the semantic model is at
              the heart of the platform. That’s not true for many other tools.
            </>
          ),
          cta: '/blog/case-study-guitar-center',
          title: 'Guitar Center',
        }}
        balanced={true}
        alignQuote="left"
        partnerText={
          <>
            <p>
              Unified its analytics use cases from Tableau, Power BI, Excel, and
              MicroStrategy into Omni for thousands of users.
            </p>
            <a
              className="call-to-action primary"
              href="/blog/case-study-guitar-center"
            >
              Read case study
            </a>
          </>
        }
      />

      <section className="compare-cards">
        <hgroup className="centered-section-title">
          <H3 fontSize="xxxl">
            How <mark>Omni</mark> & Strategy compare
          </H3>
        </hgroup>

        <Stack gap={8}>
          <CompareCard1 />
          <CompareCard2 />
        </Stack>
      </section>

      <QuoteSectionv2
        quote={{
          cite: 'Lizzy Bradford, Senior Director of Analytics',
          logo: customerLogos.buzzfeed,
          quote: (
            <>
              Creating the Omni data model was very fast compared to what we
              were used to. The generation of the initial model code in Omni is
              so easy — instantly syncing with our warehouse and having views
              available saved us a lot of time. We built a lot of curated
              datasets (Topics) and had dashboards ready in a couple of weeks.
            </>
          ),
          cta: '/blog/case-study-buzzfeed',
          title: 'BuzzFeed',
        }}
        balanced={true}
        alignQuote="left"
        partnerText={
          <>
            <p>
              Unified its analytics use cases from Tableau, Power BI, Excel, and
              MicroStrategy into Omni for thousands of users.
            </p>
            <a
              className="call-to-action primary"
              href="/blog/case-study-buzzfeed"
            >
              Read case study
            </a>
          </>
        }
      />

      <section className="compare-cards">
        <Stack gap={8}>
          <CompareCard3 />
          <CompareCard4 />
        </Stack>
      </section>

      <QuoteSectionv2
        quote={{
          cite: 'Head of Data at Cribl',
          logo: customerLogos.cribl,
          quote: (
            <>
              Omni’s deep integration with dbt allows us to expose more of that
              context to our stakeholders right where they consume data […] This
              also makes the analysts on my team happy because they do not need
              to have VS Code and Omni side-by-side as they’re developing the
              data model.
            </>
          ),
          cta: '/blog/case-study-cribl',
          title: 'Cribl',
        }}
        balanced={true}
        alignQuote="left"
        partnerText={
          <>
            <p>
              Unified its analytics use cases from Tableau, Power BI, Excel, and
              MicroStrategy into Omni for thousands of users.
            </p>
            <a className="call-to-action primary" href="/blog/case-study-cribl">
              Read case study
            </a>
          </>
        }
      />

      <section className="compare-cards">
        <Stack gap={8}>
          <CompareCard5 />
        </Stack>
      </section>

      <section className="customer-love-wall full-width">
        <div className="thunder-salmon-hearts">
          <ThunderSalmonHeart />
          <ThunderSalmonHeart />
          <ThunderSalmonHeart />
        </div>

        <div className="awww">
          <BlobbyAww />
        </div>

        <div className="excited-blob"></div>
        <hgroup className="centered-section-title">
          <h3>Customer love</h3>
        </hgroup>

        <section className="grid cols3">
          {g2Reviews.slice(0, 3).map((review) => {
            return (
              <G2ReviewCard
                hideAvatar={true}
                review={review}
                key={review.name}
              />
            )
          })}
        </section>
      </section>

      <section className="faq">
        <hgroup className="centered-section-title">
          <h3>FAQs</h3>
        </hgroup>

        <Stack gap={2} className="faq-section">
          <Accordion title="Is Strategy discontinuing on-premises?">
            <P>
              Not across the board. Support for the legacy platform ends
              December 31, 2026. Paid extended support buys two more years of
              security patches, and nothing new. After 2028, the legacy platform
              is no longer supported. Separately, the current Strategy One
              platform still ships on-premises-installable quarterly releases,
              each supported for two years from its release date. So on-premises
              isn't disappearing outright. But cloud is Strategy’s primary
              deployment model, and their own{' '}
              <a
                href="https://www.sec.gov/Archives/edgar/data/1050446/000105044626000020/mstr-20251231.htm"
                target="_blank"
                rel="noopener noreferrer"
              >
                annual report
              </a>{' '}
              says they no longer push perpetual licenses and are scaling back
              on-prem support. If you run the legacy platform, you’re choosing
              your next platform either way.
            </P>
          </Accordion>

          <Accordion title="Omni is a relatively new tool. Does it have the same features as Strategy?">
            <P>
              Strategy is a broad platform, and some of its breadth has no
              direct Omni equivalent. Where Omni differs is architecture: direct
              warehouse queries with an automatic cache instead of cubes, a
              Git-backed semantic model instead of migration packages, and AI
              built into the platform. Strategy AI is cloud-only. Those aren't
              missing features; they're different design choices. You’re
              choosing your next ten years of BI. Judge both platforms by what
              they’re building now.
            </P>
          </Accordion>

          <Accordion title="We have strict security and compliance requirements. Does Omni meet them?">
            <P>
              Omni is ISO/IEC 27001 certified and publishes SOC 2 Type II
              reports, and meets GDPR, CCPA, and HIPAA standards. Certifications
              are table stakes, though. The thing worth checking in any BI
              platform is where access rules live. In Omni they live in the
              model, so grants and filters apply on every surface, including AI,
              rather than being rebuilt per report or project.
            </P>
          </Accordion>

          <Accordion title="How much work is a Strategy-to-Omni migration, really?">
            <P>
              It’s a project, and so is Strategy’s own path. There’s no
              self-serve path from the legacy platform to their cloud; moving
              there is an expert-assisted engagement that runs through your
              Customer Success Manager. Their own brochure says the environment
              move takes two to four weeks on average. Omni pairs migrations
              with 1:1 support and training.{' '}
              <a href="/blog/case-study-guitar-center">Guitar Center</a>{' '}
              consolidated four BI tools, including Strategy, into Omni for
              thousands of users, recreating 150+ dashboards within six months.
              Bring your inventory; we’ll size it honestly.
            </P>
          </Accordion>

          <Accordion title="I built my organization on Strategy’s semantic layer. Do I lose that with Omni?">
            <P>
              No. Strategy’s semantic layer idea is right: governed definitions
              that every report builds on. Omni keeps that idea and adds Git,
              branch testing, and promotion. Three layers separate database
              schema, shared governed metrics, and ad-hoc workbooks, with a
              promotion path between them. Row- and field-level security is
              enforced on every query. The whole model lives in Git, defined in
              YAML files that follow the{' '}
              <a href="/blog/announcing-the-open-semantic-interchange">
                Apache Ossie
              </a>{' '}
              open semantic standard, and is interoperable with dbt, Snowflake
              Semantic Views, and Databricks Metric Views.
            </P>
          </Accordion>

          <Accordion title="Why do I need a semantic layer in Omni if I’m using dbt?">
            <P>
              If your team already uses dbt, Omni’s integration is why the
              semantic layer adds value, not friction. The integration is
              bi-directional: dbt metadata syncs both ways with Omni, logic
              prototyped in Omni can be written back as dbt models, exposures
              push to dbt, and Omni can query the dbt Semantic Layer. Whether a
              definition belongs in dbt or in Omni is a real question, and the{' '}
              <a href="/blog/using-omni-dbt-integration">
                integration is designed to let you move logic between them
              </a>
              . Strategy doesn't have a dbt integration.
            </P>
          </Accordion>

          <Accordion title="Is Omni or Strategy better for AI analytics?">
            <P>
              Strategy AI is cloud-only. If you’re still on the legacy platform,
              you get nothing today. Even in their cloud, building your own AI
              agents requires their Enterprise plan. In Omni, AI is part of the
              platform: queries compile through the governed model, permissions
              apply on every AI surface, and questions route through curated
              Topics. The AI answers questions, runs one-click{' '}
              <a href="/blog/introducing-omnis-agent-skills">Skills</a> for
              repeatable workflows, and drafts model changes for review with the{' '}
              <a href="/blog/announcing-omnis-modeling-agent">Modeling Agent</a>
              .
            </P>

            <P>
              Tools like ChatGPT and Claude connect through the{' '}
              <a
                href="https://docs.omni.co/ai/mcp"
                target="_blank"
                rel="noopener noreferrer"
              >
                MCP Server
              </a>{' '}
              under the same permissions. You choose the model provider, and
              admins cap AI spend by organization or user.
            </P>
          </Accordion>

          <Accordion title="We embed analytics in our product. How do Omni and Strategy compare there?">
            <P>
              Embedding Strategy means hosting and maintaining another server
              yourself. Omni embeds with{' '}
              <a
                href="https://docs.omni.co/embed"
                target="_blank"
                rel="noopener noreferrer"
              >
                signed iframe URLs
              </a>{' '}
              mapped to row-level permissions, so thousands of external users
              view one report filtered to only their own data. Security is
              enforced by the platform, not rebuilt per customer.
            </P>
          </Accordion>
        </Stack>
      </section>

      <Stack
        as="section"
        className="get-started"
        align="center"
        gap={1}
        padding={'8,0,0'}
      >
        <Blobby expression="excited" facing="left" />
        <hgroup className="centered-section-title">
          <H5 fontWeight={600} fontSize="xxxl">
            Ready to get started?
          </H5>
        </hgroup>
        <Stack row gap={2} wrap="wrap" justify="center">
          <CallToAction className="button-call-to-action" />
        </Stack>
      </Stack>
    </>
  )
}
